<template>
  <div :class="bem()">
    <LensTechOption
      v-for="(option, index) in baseOptionsToShow"
      :key="index"
      :option="option"
      :active-option="props.activeOption"
      :selected="props.activeOption?.tech === option.tech"
      :error="props.error"
      :price-offset="
        nonBlockingEquivalentOption?.priceDifference || 0
      "
      @selected="emits('selected', option)"
    />

    <div v-if="blockingOptionToShow" :class="bem('blocking-option')">
      <h4 :class="bem('option-group-title')">Lens extras</h4>
      <LensTechOption
        :option="blockingOptionToShow"
        :active-option="props.activeOption"
        :selected="activeOption?.tech === blockingOptionToShow.tech"
        :price-offset="
          blockingOptionToShow?.priceDifference || 0
        "
        subtitle="Reduce eye strain by blocking high-energy blue light from digital devices."
        :price-display-override="blockingTagText"
        @selected="handleBlueLightSelection"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  options: Array<any>
  activeOption: object
  error: boolean
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  activeOption: () => ({}),
  error: false,
})

const { $tcs } = useNuxtApp()
const bem = useBEM('EyeglassesLensTechOptions')

const emits = defineEmits(['selected'])

const baseOptionsToShow = computed(() => {
  if (props.activeOption?.isBlueLightFiltering) {
    return props.options
      .filter(
        (option) => option.isEyeglasses && ['stb', 'thb'].includes(option.tech)
      )
      .map((option) => {
        if (option.tech?.endsWith('b')) {
          // find the non blocking equivalent
          const nonBlockingEquivalent = props.options.find((opt) => {
            return option.tech.replace(/b$/, 'c') === opt.tech
          })

          // inject non-blocking option descriptions into blocking option descriptions
          return {
            ...option,
            lensTechSelectorTitleOverride: nonBlockingEquivalent.displayTitle,
            displaySubtitle: nonBlockingEquivalent.displaySubtitle,
          }
        }

        // else do nothing and just return the option
        return option
      })
  }

  return props.options.filter(
    (option) => option.isEyeglasses && ['stc', 'thc'].includes(option.tech)
  )
})

function handleBlueLightSelection() {
  /**
   * If active option is already blue light filtering then click must be to deselect it.
   *
   * Find the equivalent non-filtering variation to select
   */

  if (props.activeOption.isBlueLightFiltering && nonBlockingEquivalentOption.value) {
    emits('selected', nonBlockingEquivalentOption.value)
    return
  }

  emits('selected', blockingOptionToShow.value)
}

const blueLightOptions = computed(() => {
  return props.options.filter((option) => option.isBlueLightFiltering)
})

const blockingOptionToShow = computed(() => {
  // find blocking option that matches selected tech
  if (props.activeOption?.tech) {
    const blockingOptionThatMatchesBaseLensTech = blueLightOptions.value.find(
      (blueLightOption) =>
        blueLightOption?.tech === props.activeOption.tech.replace(/c$/, 'b')
    )

    if (blockingOptionThatMatchesBaseLensTech)
      return blockingOptionThatMatchesBaseLensTech
  }

  // else show the cheapest option first
  return [...blueLightOptions.value].sort((a, b) => a.price - b.price)[0]
})

const nonBlockingEquivalentOption = computed(() => {
  return props.options.find((option) => {
    return option.tech === props.activeOption?.tech?.replace(/b$/, 'c')
  })
})

const blockingTagText = computed(() => {
  if (blockingOptionToShow.value?.priceDifference > 0) {
    return `+ ${$tcs(blockingOptionToShow.value.priceDifference)}`
  } else if (blockingOptionToShow.value?.priceDifference < 0) {
    return `- ${$tcs(blockingOptionToShow.value.priceDifference)}`
  }

  if (nonBlockingEquivalentOption.value?.priceDifference < 0) {
    return `+ ${$tcs(
      Math.abs(nonBlockingEquivalentOption.value?.priceDifference)
    )}`
  } else if (nonBlockingEquivalentOption.value?.priceDifference > 0) {
    return `- ${$tcs(nonBlockingEquivalentOption.value?.priceDifference)}`
  }

  return $tcs(blockingOptionToShow.value.priceDifference)
})
</script>

<style lang="postcss" scoped>
.EyeglassesLensTechOptions {
  @apply flex flex-col gap-y-3;
}

.EyeglassesLensTechOptions__option-group-title {
  @apply font-fragment-mono text-xxs text-grey-600
    mb-1;
}
</style>
